import {gql, useQuery} from '@apollo/client';
import React, {useCallback, useEffect} from 'react';
import {LoadingSpinner, RelayConnection, SimpleDropdown, ErrorDisplay} from "../../common";
import {
    asQueryParam,
    ProviderEvent,
    ProviderEventColumn,
    ProviderEventListFilterValue,
    ProviderEventSortBy,
} from "./domain";
import {RelayPagination, RelayPaginationState, useRelayPagination} from "../pagination";
import {CaretDownIcon, CaretUpIcon, Icon, IconButton, Pane, RefreshIcon} from "evergreen-ui";
import {ProviderEventPreview} from "./ProviderEventPreview";
import {ProviderEventListAction} from "./providerEventListReducer";
import {SortOrder} from "../client/domain";
import {IsFilterEmpty, ProviderEventListFilter} from "./ProviderEventListFilter";
import {ProviderEventListFilterBadge} from "./ProviderEventListFilterBadge";
import {useProviderEventUnreadDispatch} from "./ProviderEventUnreadCount";
import {CurrentUserData} from "../../security";

export const PROVIDER_EVENT_LIST = gql`
query providerEventsQuery($first: Int = 20, $after: String, $params: ProviderEventQueryParamsInput!) {
    providerEventsQuery(
        first: $first
        after: $after
        params: $params
    ) {
        edges {
            cursor
            node {
                guid
                eventType
                eventTime
                viewedAt
                viewedBy {
                    guid
                    firstName
                    lastName
                }
                client {
                    guid
                    fullName
                }
                linkedClients {
                    guid
                    fullName
                }
                clientReport {
                    guid
                    reportType
                    reportStatus
                    clientReportNotifyUsers {
                      guid
                      firstName
                      lastName
                    }
                    submittedBy {
                        firstName
                        lastName
                        guid
                    }
                }
                clientDocumentType
                authorization {
                    guid
                    bhpnAuthNumber
                }
                authChangedSections
                caseSfId
                caseNumber
                holdEndDate
                holdReminderNumber
                holdActionCompletedAt
            }
        }
        totalElements
        pageInfo {
            hasPreviousPage
            endCursor
            hasNextPage
            startCursor
        }
    }
}
 `

export type ProviderEventQueryResponse = RelayConnection<ProviderEvent> & { totalElements: number };


export const ProviderEventColumns: Readonly<ProviderEventColumn[]> = [
    // @ts-ignore
    {name: 'Description', attr: '_description'},
    {name: 'Notification Type', attr: 'eventType'},
    {name: 'Notification Date', attr: 'eventTime'},
    {name: 'Viewed Date', attr: 'viewedAt'},
    {name: 'Viewed By', attr: 'viewedBy'},
    {name: 'Report Submitted By', attr: 'clientReport.submittedBy'},
    {name: 'Actions', attr: '_actions'},
]

type ProviderEventListParams = {
    selected: ProviderEvent | null,
    sortBy: ProviderEventSortBy;
    currentUser: CurrentUserData | null,
    markEventAsReadFunc: (eventGuids: string[]) => void;
    pagination: RelayPaginationState;
    filterValue: ProviderEventListFilterValue,
    dispatch: React.Dispatch<ProviderEventListAction>;
    pageSize: number;
    onConfirmEndDateClick?: (providerEvent: ProviderEvent) => void;
}


export const ProviderEventList = ({
                                      currentUser,
                                      selected,
                                      sortBy,
                                      pagination,
                                      markEventAsReadFunc,
                                      dispatch,
                                      filterValue,
                                      pageSize,
                                      onConfirmEndDateClick,
                                  }: ProviderEventListParams) => {
    const providerEventUnreadDispatch = useProviderEventUnreadDispatch();

    const {loading, data, error, refetch} = useQuery<{ providerEventsQuery: ProviderEventQueryResponse }, {}>(
        PROVIDER_EVENT_LIST, {
            fetchPolicy: "no-cache",
            notifyOnNetworkStatusChange: true,
            variables: {
                ...pagination.toGraphQlParams(),
                params: {
                    ...asQueryParam(filterValue),
                    sortDir: sortBy.order,
                    sortBy: sortBy.column,
                },
            },
            onCompleted: () => {
                dispatch({type: "SET_REFETCH_FUNC", refetchFunc: refetch});
            }
        }
    );

    const refreshNotifications = useCallback(async () => {
        providerEventUnreadDispatch({type: "SET_REFRESH"});
        await refetch();
    }, [providerEventUnreadDispatch, refetch]);

    // polling every 5 minutes
    useEffect(() => {
        const polling = setInterval(() => {
            refreshNotifications().catch((err) => console.log("Failed to re-fetch new notification by error:  ", err))
        }, 5 * 60 * 1000);
        return () => clearInterval(polling);
    }, [refreshNotifications])

    const setSortByColumn = (newSortColumn: string) => {
        // ignore sort by event type (description)
        if (["eventType", "clientReport.submittedBy"].includes(newSortColumn)) {
            return;
        }

        let newSortOrder = SortOrder.DESC;
        if (newSortColumn === sortBy.column && sortBy.order === newSortOrder) {
            newSortOrder = SortOrder.ASC;
        }

        dispatch({type: 'SET_SORT_BY', sortBy: {column: newSortColumn, order: newSortOrder}});
    }

    let totalEvents: number = 0;
    if (data?.providerEventsQuery?.pageInfo?.endCursor !== null) {
        totalEvents = data?.providerEventsQuery?.totalElements ?? 0;
    }
    const {onPaginationClick} = useRelayPagination(dispatch, totalEvents);

    if (error) {
        return <ErrorDisplay error={error} title="Error loading provider events" />;
    }

    return (
        <Pane className="h-full pt-5 m-5 bg-white shadow-md flex flex-col"
              minHeight="calc(100vh - 100px - 260px - 40px)">
            <Pane display='flex' justifyContent='flex-end' flex="0 0" paddingBottom={20}>
                <div className="flex items-center">
                    <ProviderEventListFilter initFilterValue={filterValue} dispatch={dispatch}/>
                    <SimpleDropdown options={["10", "50", "100"]} selected={pageSize.toString()}
                                    minWidth={"2rem"}
                                    textColor={"gray-700"}
                                    setSelected={(newPageSize) => dispatch({
                                        type: "SET_PAGE_SIZE",
                                        pageSize: parseInt(newPageSize)
                                    })}/>
                    <RelayPagination pageInfo={data?.providerEventsQuery.pageInfo} count={totalEvents}
                                     onClick={onPaginationClick}/>
                    <IconButton
                        marginX={8}
                        icon={RefreshIcon}
                        iconSize={18}
                        disabled={loading}
                        onClick={refreshNotifications}
                    />
                </div>
            </Pane>
            <ProviderEventListFilterBadge filter={filterValue} dispatch={dispatch}/>
            <div className="overflow-auto flex flex-col flex-grow mb-4 scrollbar-always">
                <table className="table-fixed w-full">
                    <thead>
                    <tr className='text-left select-none text-sm text-gray-portal-table-title'>
                        {
                            ProviderEventColumns.map((column) => (
                                <th
                                    key={column.attr}
                                    className={'font-light ' + (column.attr === '_description' ? 'pl-8 w-6/12' : 'w-2/12')}
                                    onClick={() => setSortByColumn(column.attr)}
                                >
                                    <div className='flex items-center'>
                                        <div>
                                            {column.name}
                                        </div>
                                        <div className='w-6 h-6 pl-2'>
                                            {sortBy.column === column.attr && sortBy.order === SortOrder.ASC &&
                                                <Icon className='align-middle' icon={CaretUpIcon}/>}
                                            {sortBy.column === column.attr && sortBy.order === SortOrder.DESC &&
                                                <Icon className='align-middle' icon={CaretDownIcon}/>}
                                        </div>
                                    </div>
                                </th>
                            ))
                        }
                    </tr>
                    </thead>
                    <tbody>
                    {
                        !loading && data!.providerEventsQuery.edges.map((edge) => edge.node).map((providerEvent) => (
                            <ProviderEventPreview
                                currentUser={currentUser}
                                markEventAsReadFunc={markEventAsReadFunc}
                                selected={selected?.guid === providerEvent.guid}
                                dispatch={dispatch}
                                key={providerEvent.guid}
                                providerEvent={providerEvent}
                                onConfirmEndDateClick={onConfirmEndDateClick}
                            />
                        ))
                    }
                    </tbody>
                </table>
                {
                    loading &&
                    <div className="w-full h-full flex flex-grow items-center">
                        <LoadingSpinner spacing={20} height={"100%"}/>
                    </div>
                }
                {
                    totalEvents === 0 && !loading && !IsFilterEmpty(filterValue) &&
                    <div className="w-full h-full flex flex-grow items-center">
                        <div className="m-auto">
                            No notifications matched the searching criteria.
                        </div>
                    </div>
                }
            </div>
        </Pane>
    );
}
